import * as React from 'react'
import { SectionList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import Checkbox from '@components/checkbox/Checkbox'
import Tabs from '@components/tabs/Tabs'
import Icons from '@components/icon/Icons'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'

import useI18n from '@i18n/useI18n'

import useBreakpoints from '@utils/useBreakpoints'

import { FormikHelpers, useFormik } from 'formik'

import { TypeCanalEnum, TypeEvenementEnum, TypeIncidentEnum } from '@api/enum'

interface Props {
  form: PreferenceNotificationRequest[]
  onForm: (form: PreferenceNotificationRequest[]) => Promise<void>
}

interface Section {
  title: string
  data: typeof options
  siteId: string
}

const options: readonly TypeEvenementEnum[] = [
  TypeEvenementEnum.OUVERTURE_INCIDENT,
  TypeEvenementEnum.FERMETURE_INCIDENT,
  TypeEvenementEnum.CHANGEMENT_ETAT_INCIDENT,
  TypeEvenementEnum.AJOUT_COMMENTAIRE,
] as const

const renderTitle = ({ section: { title } }: { section: Section }) => (
  <TitleView>
    <Title>{title}</Title>
  </TitleView>
)

const getStatus = (mail: boolean, push: boolean, sms: boolean) => {
  const keys: string[] = []

  if (mail) {
    keys.push('mail')
  }
  if (push) {
    keys.push('push')
  }
  if (sms) {
    keys.push('sms')
  }

  return keys.length > 0 ? keys.join('_') : 'none'
}

const Notification = ({ form, onForm }: Props) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoints()
  const isPhone = bp === 'phone'

  const sites = useReducer(SiteStore.store, (s) => s.sites)
  const tabs = sites.map((s) => ({ label: i18n.t(`screens.site.sites.${s.codeLieu}`), value: s.id }))
  const [tab, setTab] = React.useState(tabs[0])

  const [typeIncidents, setTypeIncidents] = React.useState<TypeIncidentEnum[]>(
    Array.from(new Set(form.map((pref) => pref.typeIncident).filter(Boolean))) as TypeIncidentEnum[]
  )

  const choicesDisabled = typeIncidents.length === 0
  React.useEffect(() => {
    const cleaned = values.reduce((acc, cur) => {
      if (
        !acc.find(
          (pref) =>
            pref.siteId === cur.siteId && pref.typeCanal === cur.typeCanal && pref.typeEvenement === cur.typeEvenement
        )
      ) {
        acc.push({ ...cur, typeIncident: undefined })
      }
      return acc
    }, [] as PreferenceNotificationRequest[])

    const newValues = typeIncidents.reduce((acc, cur) => {
      const allItemsForType = cleaned.map((pref) => ({ ...pref, typeIncident: cur }))
      return [...acc, ...allItemsForType]
    }, [] as PreferenceNotificationRequest[])
    setValues(newValues)
  }, [typeIncidents])

  const data: Section[] = React.useMemo(
    () =>
      sites.map((site) => ({ title: i18n.t(`screens.site.sites.${site.codeLieu}`), siteId: site.id, data: options })),
    [sites]
  )
  const scroll = React.useRef<SectionList<(typeof options)[number], Section>>(null)

  const onSubmit = (
    vals: PreferenceNotificationRequest[],
    { setSubmitting }: FormikHelpers<PreferenceNotificationRequest[]>
  ) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setValues, handleSubmit, dirty } = useFormik<PreferenceNotificationRequest[]>({
    enableReinitialize: true,
    initialValues: form,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  React.useEffect(() => {
    if (scroll.current && data.length > 0) {
      const index = tabs.findIndex((t) => t.value === tab.value)
      scroll.current.scrollToLocation({
        sectionIndex: index,
        itemIndex: 0,
        animated: true,
        viewOffset: index === 0 ? 0 : -20,
      })
    }
  }, [tab])

  const toggleItem = (
    opt: (typeof options)[number],
    type: TypeCanalEnum,
    siteId: string,
    prev?: PreferenceNotificationRequest
  ) => {
    if (prev) {
      setValues(
        values.filter(
          (v) => v.siteId !== prev.siteId || v.typeCanal !== prev.typeCanal || v.typeEvenement !== prev.typeEvenement
        )
      )
    } else {
      const items: PreferenceNotificationRequest[] = typeIncidents.map((typeIncident) => ({
        siteId,
        typeIncident,
        typeCanal: type,
        typeEvenement: opt,
      }))

      setValues([...values, ...items])
    }
  }

  const renderItem = (opt: (typeof options)[number]) => {
    const eventValues = values.filter((v) => v.siteId === tab.value)
    const mail = eventValues.find((v) => v.typeEvenement === opt && v.typeCanal === TypeCanalEnum.MAIL)
    const push = eventValues.find((v) => v.typeEvenement === opt && v.typeCanal === TypeCanalEnum.PUSH)
    const sms = eventValues.find((v) => v.typeEvenement === opt && v.typeCanal === TypeCanalEnum.SMS)

    return (
      <NotificationContainer key={opt}>
        <NotificationTitle>{i18n.t(`screens.account.notificationSection.items.${opt}`)}</NotificationTitle>
        <CheckboxView>
          <CheckboxContainer>
            <Checkbox
              label={i18n.t('screens.account.notificationSection.mail')}
              onClick={() => toggleItem(opt, TypeCanalEnum.MAIL, tab.value, mail)}
              value={!!mail}
              disabled={choicesDisabled}
            />
          </CheckboxContainer>
          <CheckboxContainer>
            <Checkbox
              label={i18n.t('screens.account.notificationSection.push')}
              onClick={() => toggleItem(opt, TypeCanalEnum.PUSH, tab.value, push)}
              value={!!push}
              disabled={choicesDisabled}
            />
          </CheckboxContainer>
          <CheckboxContainer>
            <Checkbox
              label={i18n.t('screens.account.notificationSection.sms')}
              onClick={() => toggleItem(opt, TypeCanalEnum.SMS, tab.value, sms)}
              value={!!sms}
              disabled={choicesDisabled}
            />
          </CheckboxContainer>
        </CheckboxView>
      </NotificationContainer>
    )
  }

  const renderMobileItem = ({
    item,
    index,
    section,
  }: {
    item: (typeof options)[number]
    index: number
    section: Section
  }) => {
    const eventValues = values.filter((v) => v.siteId === section.siteId)

    const mail = eventValues.find((v) => v.typeEvenement === item && v.typeCanal === TypeCanalEnum.MAIL)
    const push = eventValues.find((v) => v.typeEvenement === item && v.typeCanal === TypeCanalEnum.PUSH)
    const sms = eventValues.find((v) => v.typeEvenement === item && v.typeCanal === TypeCanalEnum.SMS)

    return (
      <Background>
        <NotificationContainer isLastItem={index === data.length - 1}>
          <TitleContainer>
            <NotificationTitle>{i18n.t(`screens.account.notificationSection.mobileItems.${item}`)}</NotificationTitle>
            <Status>{i18n.t(`screens.account.notificationSection.status.${getStatus(!!mail, !!push, !!sms)}`)}</Status>
          </TitleContainer>
          <Container>
            <TouchableOpacity onPress={() => toggleItem(item, TypeCanalEnum.MAIL, tab.value, mail)}>
              <Icons name="mail" color={!!mail ? theme.colors.primary : theme.colors.disable} size={24} />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => toggleItem(item, TypeCanalEnum.PUSH, tab.value, push)}>
              <Icons name="mobile" color={!!push ? theme.colors.primary : theme.colors.disable} size={24} />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => toggleItem(item, TypeCanalEnum.SMS, tab.value, sms)}>
              <Icons name="comment" color={!!sms ? theme.colors.primary : theme.colors.disable} size={24} />
            </TouchableOpacity>
          </Container>
        </NotificationContainer>
      </Background>
    )
  }

  const toggleTypeIncident = (incidentKey: TypeIncidentEnum, newValue: boolean) => {
    if (newValue) {
      setTypeIncidents([...typeIncidents, incidentKey])
    } else {
      setTypeIncidents(typeIncidents.filter((pref) => pref !== incidentKey))
    }
  }

  const renderCheckbox = (incidentKey: TypeIncidentEnum) => {
    const value = !!typeIncidents.find((type) => type === incidentKey)
    return (
      <CheckboxContainer key={incidentKey}>
        <Checkbox
          label={i18n.t(`screens.account.notificationSection.typeIncident.${incidentKey}`)}
          value={value}
          onClick={() => toggleTypeIncident(incidentKey, !value)}
        />
      </CheckboxContainer>
    )
  }

  if (sites.length > 0) {
    return (
      <MainContainer>
        <IncidentChoice>
          <IncidentIndication>{i18n.t('screens.account.notificationSection.incident')}</IncidentIndication>
          <Checkboxes>
            {renderCheckbox(TypeIncidentEnum.TOUTE)}
            {renderCheckbox(TypeIncidentEnum.SUIVI)}
            {renderCheckbox(TypeIncidentEnum.CREE)}
          </Checkboxes>
        </IncidentChoice>

        <Sites disabled={choicesDisabled}>
          <Head>
            {isPhone ? (
              <Tabs disabled={choicesDisabled} data={tabs} onItemSelected={setTab} value={tab} />
            ) : (
              <SegmentedControl disabled={choicesDisabled} data={tabs} onItemSelected={setTab} value={tab} />
            )}
          </Head>

          {isPhone ? (
            <SectionList sections={data} renderSectionHeader={renderTitle} renderItem={renderMobileItem} ref={scroll} />
          ) : (
            options.map(renderItem)
          )}
        </Sites>
      </MainContainer>
    )
  } else {
    return <NoInfo>{i18n.t('screens.account.notificationSection.noSite')}</NoInfo>
  }
}

export default Notification

const MainContainer = styled(View)`
  ${(props) => props.theme.breakpoint === 'phone' && `flex: 1;`}
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.backgroundSecondary : props.theme.colors.background};
`
const Background = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  padding: 0 16px;
`

const NotificationContainer = styled(View)<{ isLastItem?: boolean }>`
  flex-direction: row;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom-width: ${(props) => (props.isLastItem ? 0 : 1)}px;
  border-bottom-color: ${(props) => props.theme.colors.disable};
  background-color: ${(props) => props.theme.colors.background};
`

const NotificationTitle = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const CheckboxView = styled(View)`
  flex-direction: row;
`

const CheckboxContainer = styled(View)`
  margin-right: 24px;
`

const Head = styled(View)`
  margin-top: 10px;
  margin-bottom: 24px;
`

const TitleContainer = styled(View)`
  flex: 1;
  margin-right: 24px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-top: 24px;
  margin-bottom: 8px;
  margin-left: 16px;
`

const Container = styled(View)`
  flex-direction: row;
  width: 90px;
  justify-content: space-between;
  align-items: center;
`

const Status = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.secondaryText};
`

const NoInfo = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.disable};
  margin-top: 16px;
  text-align: ${(props) => (props.theme.breakpoint === 'phone' ? `center` : `left`)};
`

const TitleView = styled(View)`
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const IncidentChoice = styled(View)`
  flex-direction: ${(props) =>
    props.theme.breakpoint === 'phone' || props.theme.breakpoint === 'medium' || props.theme.breakpoint === 'mediumBig'
      ? 'column'
      : 'row'};
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? '16px;' : '24px 0px')};
`

const IncidentIndication = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex: 1;`}
  min-width: 240px;
  margin-bottom: 8px;
`

const Checkboxes = styled(View)`
  flex-direction: ${(props) =>
    props.theme.breakpoint === 'phone' || props.theme.breakpoint === 'medium' ? 'column' : 'row'};
  margin-bottom: 8px;
`

const Sites = styled(View)<{ disabled: boolean }>`
  ${(props) => props.disabled && `opacity: 0.5;`};
  ${(props) => props.theme.breakpoint === 'phone' && `flex: 1;`}
`
